/**
 * =============================================================================
 * AppSidebar.tsx - Sidebar di navigazione globale
 * =============================================================================
 * 
 * Sidebar verticale che sostituisce la navbar orizzontale.
 * Usa Shadcn Sidebar con collapsible="icon" per desktop e Sheet per mobile.
 * Include navigazione, notifiche, simulatore ruoli e azioni utente.
 * 
 * @author CCFN Development Team
 * =============================================================================
 */

import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BookMarked,
  BookOpen,
  BookText,
  Link2,
  MessageSquare,
  HelpCircle,
  Presentation,
  LayoutDashboard,
  Settings,
  UserPlus,
  ClipboardList,
  LogOut,
  LogIn,
  UserPlusIcon,
  Download,
  User // Aggiunto per il Profilo
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth, type AppRole } from "@/hooks/useAuth";
import { RoleSimulator } from "@/components/RoleSimulator";
import { ForumNotificationBell } from "@/components/forum/ForumNotificationBell";
import { UserNotificationBell } from "@/components/notifications/UserNotificationBell";
import { TenantLogo } from "@/components/layout/TenantLogo";
import { useTour } from "@/contexts/TourContext";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useTenantBrand } from "@/hooks/useTenantBrand";


// ---------- Navigation items ----------

interface NavItem {
  title: string;
  url: string;
  icon: React.ElementType;
  /** Show only when user is logged in */
  requiresAuth?: boolean;
  /** Minimum role level required */
  minRole?: AppRole;
  /** Show only for non-members with LNI workflow */
  membershipOnly?: boolean;
  /** Feature flag gate */
  featureFlag?: string;
  /** data-tour-id */
  tourId?: string;
  /** Override URL for admin/formatore+ users */
  adminUrl?: string;
}

const NAV_ITEMS: NavItem[] = [
  // Gestione viene mostrata solo a formatore+ ed è collocata in alto
  { title: "Gestione", url: "/admin", icon: Settings, requiresAuth: true, minRole: "formatore", tourId: "nav-admin" },
  { title: "Pannello", url: "/dashboard", icon: LayoutDashboard, requiresAuth: true, tourId: "nav-dashboard" },
  { title: "Corsi", url: "/corsi", icon: BookOpen, requiresAuth: true, tourId: "nav-courses", adminUrl: "/admin/corsi" },
  { title: "Glossario", url: "/glossario", icon: BookText, requiresAuth: true, tourId: "nav-glossary" },
  { title: "Bibliografia", url: "/bibliografia", icon: BookMarked, requiresAuth: true, tourId: "nav-bibliography" },
  { title: "Collegamenti", url: "/link-utili", icon: Link2, requiresAuth: true, tourId: "nav-useful-links" },
  { title: "Forum", url: "/forum", icon: MessageSquare, requiresAuth: true, tourId: "nav-forum" },
  { title: "Guida", url: "/guida", icon: HelpCircle, requiresAuth: true, tourId: "nav-guide" },
  { title: "Presentazione portale", url: "/benvenuto", icon: Presentation, requiresAuth: true, tourId: "nav-portal-presentation" },
  { title: "Esporta", url: "/admin/esporta-contenuti", icon: Download, requiresAuth: true, minRole: "responsabile", tourId: "nav-export" },
  { title: "Iscriviti", url: "/apply", icon: UserPlus, membershipOnly: true, tourId: "nav-become-member" },
  { title: "Candidature", url: "/candidature", icon: ClipboardList, requiresAuth: true, minRole: "segreteria", featureFlag: "feature_lni_workflow", tourId: "nav-applications" },
  { title: "Profilo", url: "/profilo", icon: User, requiresAuth: true, tourId: "nav-profile" },
];

export function AppSidebar() {
  const { state, setOpen } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, hasRoleLevel } = useAuth();
  const { startTour } = useTour();
  const lniWorkflowEnabled = useFeatureFlag("feature_lni_workflow");
  const { displayName: displayBrandName } = useTenantBrand();
  const [membershipStatus, setMembershipStatus] = useState<string | null>(null);

  const isCoursePage = location.pathname.startsWith("/corso/") || location.pathname.startsWith("/corsi/");
  const prevIsCoursePageRef = useRef(false);

  useEffect(() => {
    if (isCoursePage && !prevIsCoursePageRef.current) {
      setOpen(false);
    }
    prevIsCoursePageRef.current = isCoursePage;
  }, [isCoursePage, setOpen]);

  useEffect(() => {
    if (user?.id) {
      supabase
        .from("profiles")
        .select("membership_status")
        .eq("id", user.id)
        .single()
        .then(({ data }) => setMembershipStatus(data?.membership_status || null));
    } else {
      setMembershipStatus(null);
    }
  }, [user?.id]);

  const isNonMember = user && membershipStatus === "non_member";

  const isActive = (url: string) => {
    if (url === "/") return location.pathname === "/";
    return location.pathname.startsWith(url);
  };

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.requiresAuth && !user) return false;
    if (item.minRole && !hasRoleLevel(item.minRole)) return false;
    if (item.membershipOnly) {
      if (!isNonMember || !lniWorkflowEnabled) return false;
    }
    if (item.featureFlag === "feature_lni_workflow" && !lniWorkflowEnabled) return false;
    return true;
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleStartTour = () => {
    if (location.pathname === "/dashboard") startTour("dashboard", true);
    else if (location.pathname === "/admin") startTour("admin", true);
    else if (location.pathname.startsWith("/corsi/")) startTour("course", true);
    else startTour("dashboard", true);
  };

  return (
    <Sidebar collapsible="icon" data-tour-id="navbar" className="[&_[data-sidebar=menu]]:gap-0 [&_[data-sidebar=group-label]]:py-0.5 [&_[data-sidebar=menu-button]]:h-7 [&_[data-sidebar=menu-button]]:text-xs">
      {/* ---------- Header: Logo ---------- */}
      <SidebarHeader className="p-3">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label={displayBrandName} className="flex items-center gap-2 group min-w-0">
            <TenantLogo className="w-8 h-8 object-contain shrink-0 group-hover:scale-105 transition-transform" />
            {!collapsed && (
              <span className="font-display text-lg font-semibold text-sidebar-foreground truncate">
                {displayBrandName}
              </span>
            )}
          </Link>
          <SidebarTrigger className="text-muted-foreground hover:text-foreground shrink-0" />
        </div>
      </SidebarHeader>

      <SidebarSeparator />

      {/* ---------- Navigation ---------- */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigazione</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => {
                const resolvedUrl = item.adminUrl && hasRoleLevel("formatore") ? item.adminUrl : item.url;
                return (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(resolvedUrl)}
                    tooltip={item.title}
                  >
                    <Link to={resolvedUrl} data-tour-id={item.tourId}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Quick actions */}
        {user && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Azioni</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleStartTour}
                      tooltip="Tour guidato"
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>Tour guidato</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>

                {!collapsed && (
                  <div className="flex items-center gap-1 px-2 py-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span><UserNotificationBell /></span>
                      </TooltipTrigger>
                      <TooltipContent side="right">Notifiche di sistema</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span><ForumNotificationBell /></span>
                      </TooltipTrigger>
                      <TooltipContent side="right">Notifiche forum</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarSeparator />

      {/* ---------- Footer: User info ---------- */}
      <SidebarFooter className="p-3">
        {user ? (
          <div className="space-y-2">
            {!collapsed && <RoleSimulator />}

            {!collapsed && (
              <div className="px-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate" title={user.user_metadata?.full_name || user.email}>
                  {user.user_metadata?.full_name || user.email?.split('@')[0]}
                </p>
                <p className="text-[11px] text-muted-foreground truncate" title={user.email}>
                  {user.email}
                </p>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="w-full justify-start"
              data-tour-id="btn-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {!collapsed && "Esci"}
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <Button variant="ghost" size="sm" asChild className="w-full justify-start" data-tour-id="btn-login">
              <Link to="/auth">
                <LogIn className="h-4 w-4 mr-2" />
                {!collapsed && "Accedi"}
              </Link>
            </Button>
            <Button size="sm" className="w-full justify-start bg-gradient-ocean hover:opacity-90" asChild data-tour-id="btn-register">
              <Link to="/auth?mode=signup">
                <UserPlusIcon className="h-4 w-4 mr-2" />
                {!collapsed && "Registrati"}
              </Link>
            </Button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}